import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { GroupBlurb } from './HelpPanelDrawer';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import type { PayerEnrollmentStatus, PayerRow } from './types';

export interface IntakePayerTableProps {
  canManage?: boolean;
  derivedSentence?: string | null;
  blurb?: string | null;
  help?: ReactNode;
  loading?: boolean;
  /** Empty-state copy while the actor may edit the roster. */
  manageEmptyMessage?: string;
  /** Chip copy while the actor may edit the roster. */
  manageLabel?: string;
  /** Chip copy while the roster is read-only here (e.g. the aggregate org view). */
  readOnlyLabel?: string;
  onEnrollmentStatusChange?: (rowId: string, status: PayerEnrollmentStatus) => void;
  onRemove?: (rowId: string) => void;
  onServiceChange?: (rowId: string, service: 'jotcred' | 'jotbill', enabled: boolean) => void;
  rows: PayerRow[];
  search?: ReactNode;
  /** Save-state indicator shown beside the heading (providers autosave). */
  status?: ReactNode;
}

export function IntakePayerTable({
  canManage = false,
  derivedSentence,
  blurb,
  help,
  loading = false,
  manageEmptyMessage = 'No payers configured. Search above to add the first payer.',
  manageLabel,
  readOnlyLabel = 'read-only here',
  onEnrollmentStatusChange,
  onRemove,
  onServiceChange,
  rows,
  search,
  status,
}: IntakePayerTableProps) {
  const renderSection = (status: PayerEnrollmentStatus, heading: string, emptyMessage: string) => {
    const sectionRows = rows.filter(row => row.enrollmentStatus === status);
    const otherStatus: PayerEnrollmentStatus =
      status === 'already_enrolled' ? 'wants_to_enroll' : 'already_enrolled';
    const otherLabel =
      status === 'already_enrolled' ? 'Wants to get credentialed' : 'Credentialed elsewhere';
    // The segmented toggle always has exactly one option selected: jotcred is
    // derived from the section (off when credentialed elsewhere, on when the
    // payer wants to get credentialed), so only jotbill is user-toggleable.
    const serviceOptions =
      status === 'already_enrolled'
        ? { withoutBill: 'none', withBill: 'Bill only' }
        : { withoutBill: 'Cred only', withBill: 'Cred + Bill' };

    return (
      <Box component="section" aria-labelledby={`payer-roster-${status}-heading`} sx={{ mb: 2 }}>
        <Typography
          component="h3"
          id={`payer-roster-${status}-heading`}
          sx={{ color: wf.green, fontSize: '10.5px', fontWeight: 700, mb: 0.25 }}
        >
          {heading}
        </Typography>
        {sectionRows.length === 0 ? (
          <Typography sx={{ color: wf.muted, fontSize: '12px', py: 1 }}>{emptyMessage}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              aria-labelledby={`payer-roster-${status}-heading`}
              sx={{
                width: '100%',
                minWidth: 620,
                borderCollapse: 'collapse',
                fontSize: '12.5px',
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  {[
                    ['Payer', '48%'],
                    ['Payer ID', '18%'],
                    ['JotPsych services', '34%'],
                  ].map(([columnHeading, width]) => (
                    <Box
                      component="th"
                      key={columnHeading}
                      scope="col"
                      sx={{
                        borderBottom: `1px solid ${wf.line}`,
                        color: wf.muted,
                        fontSize: '10px',
                        letterSpacing: '.06em',
                        p: '6px 8px',
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        width,
                      }}
                    >
                      {columnHeading}
                    </Box>
                  ))}
                  {canManage && <Box component="th" aria-label="Actions" />}
                </Box>
              </Box>
              <Box component="tbody">
                {sectionRows.map(row => (
                  <Box component="tr" key={row.rowId}>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '7px 8px' }}
                    >
                      {row.displayName}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        borderBottom: `1px solid ${wf.lineSoft}`,
                        color: wf.muted,
                        p: '7px 8px',
                      }}
                    >
                      {row.payerId}
                    </Box>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '2px 8px' }}
                    >
                      <ToggleButtonGroup
                        aria-label={`${row.displayName} JotPsych services`}
                        color="primary"
                        disabled={!canManage}
                        exclusive
                        onChange={(_event, next: 'without_bill' | 'with_bill' | null) => {
                          if (next === null) return;
                          onServiceChange?.(row.rowId, 'jotbill', next === 'with_bill');
                        }}
                        size="small"
                        sx={{
                          my: '3px',
                          '& .MuiToggleButton-root': {
                            fontSize: '11.5px',
                            lineHeight: 1.2,
                            px: '10px',
                            py: '4px',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        value={row.jotbill ? 'with_bill' : 'without_bill'}
                      >
                        <ToggleButton value="without_bill">
                          {serviceOptions.withoutBill}
                        </ToggleButton>
                        <ToggleButton value="with_bill">{serviceOptions.withBill}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {canManage && (
                      <Box
                        component="td"
                        sx={{
                          borderBottom: `1px solid ${wf.lineSoft}`,
                          p: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={`Move to ${otherLabel}`}>
                          <IconButton
                            aria-label={`Move ${row.displayName} to ${otherLabel}`}
                            onClick={() => onEnrollmentStatusChange?.(row.rowId, otherStatus)}
                            size="small"
                            sx={{ color: wf.faint }}
                          >
                            <SwapVertIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          aria-label={`Remove ${row.displayName}`}
                          onClick={() => onRemove?.(row.rowId)}
                          size="small"
                          sx={{ color: wf.faint }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ ...cardSx, gridColumn: '1 / -1' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
        <Box sx={groupTitleSx}>Your payer list</Box>
        {help}
        {(canManage ? manageLabel : readOnlyLabel) && (
          <Box sx={chipSx('mut')}>{canManage ? manageLabel : readOnlyLabel}</Box>
        )}
        {status}
      </Box>
      <GroupBlurb blurb={blurb} sx={{ mt: '-6px', mb: '10px' }} />

      {canManage && search && <Box sx={{ maxWidth: 800, mb: 1.5 }}>{search}</Box>}

      {loading ? (
        <Typography role="status" sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          Loading payer list…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          {canManage
            ? manageEmptyMessage
            : 'No payers yet. Your payer list is where you tell us which payers to file with.'}
        </Typography>
      ) : (
        <>
          {renderSection(
            'already_enrolled',
            'Credentialed elsewhere',
            'No payers are marked as credentialed elsewhere.',
          )}
          {renderSection(
            'wants_to_enroll',
            'Wants to get credentialed',
            'No payers are waiting to get credentialed.',
          )}
        </>
      )}

      <Typography sx={{ color: wf.muted, fontSize: '11.5px', mt: 1.25 }}>
        Provider enrollment status is tracked separately for each payer and service.
      </Typography>

      {derivedSentence && (
        <Box
          sx={{
            mt: 1,
            fontSize: '11.5px',
            color: wf.amberWarn,
            bgcolor: wf.amberWarnSoft,
            borderRadius: '6px',
            p: '8px 10px',
          }}
        >
          {derivedSentence}
        </Box>
      )}
    </Box>
  );
}
